import { useQuery } from "react-query";
import {
	fetchTicket,
	fetchTicketDocuments,
	fetchTicketLogs,
	fetchTickets,
	fetchTicketMessages,
} from "../queries/tickets.queries";
import type { PaginatedFilter } from "../types/axios.types";
import type { Center } from "../types/center.types";

export const useTickets = (
	page?: number,
	size?: number,
	search?: string,
	filters?: PaginatedFilter,
	sortBy?: string[],
) => {
	const query = useQuery(
		["tickets", page, size, search, filters, sortBy],
		() => {
			try {
				return fetchTickets(page, size, search, filters, sortBy);
			} catch (err) {
				return null;
			}
		},
		{ keepPreviousData: true },
	);
	return query?.data;
};

export const useCenterTickets = ({ id }: Pick<Center, "id">) => {
	return (
		page?: number,
		size?: number,
		search?: string,
		filters?: PaginatedFilter,
		sortBy?: string[],
	) => {
		const computedFilters = { ...filters, center: id.toString() };
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const query = useQuery(
			["tickets", page, size, search, computedFilters, sortBy],
			() => {
				try {
					return fetchTickets(page, size, search, computedFilters, sortBy);
				} catch (err) {
					return null;
				}
			},
			{ keepPreviousData: true },
		);
		return query?.data;
	};
};

export const useTicket = (id?: number | string) => {
	const query = useQuery(
		["tickets", id],
		() => {
			try {
				return fetchTicket(id);
			} catch (err) {
				return null;
			}
		},
		{ enabled: id !== undefined },
	);
	return query?.data;
};

export const useTicketDocuments = (id?: number | string) => {
	const query = useQuery(["tickets", "documents", id], () => {
		try {
			return fetchTicketDocuments(id);
		} catch (err) {
			return null;
		}
	});
	return query?.data;
};

export const useTicketLogs = (id?: number | string) => {
	const query = useQuery(["tickets", "logs", id], () => {
		try {
			return fetchTicketLogs(id);
		} catch (err) {
			return null;
		}
	});
	return query?.data;
};

export const useTicketMessages = (id?: number | string) => {
	const query = useQuery(
		["tickets", "messages", id],
		() => {
			try {
				return fetchTicketMessages(id);
			} catch (err) {
				return null;
			}
		},
		{ enabled: id !== undefined },
	);
	return query;
};
